<template>
  <div
    class="relative flex min-w-0 border-b px-5"
    :class="{ 'cursor-pointer': isRowClickable }"
    @click="openModal"
  >
    <div
      class="sticky left-0 z-10 flex h-[5.3rem] w-110 flex-wrap items-center justify-between space-x-8 border-r bg-white py-5 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
    >
      <div v-if="showOfferName" class="w-max">
        {{ offerDetail.details.offer_template.name }}
      </div>
      <lf-checkbox
        v-else-if="multiSelect"
        v-model="isOfferSelected"
        :value="offerDetail"
        name="offer-checkbox"
        data-cy="offer-checkbox"
        class="w-max"
      >
        {{ offerDetail.details.offer_template.name }}
      </lf-checkbox>
      <template v-else-if="singleSelect && selectOneKey">
        <input
          v-model="isOfferSelected"
          type="radio"
          name="selectOne"
          data-cy="offer-radio-button"
          :value="`${offerDetail.id}_${offerDetail[selectOneKey]}`"
        />
        <table-select-details
          v-if="typeof selectDetailsProvider === 'function'"
          :options="selectDetailsProvider(offerDetail)"
        />
        <component
          :is="selectDetailsProvider"
          v-else-if="typeof selectDetailsProvider === 'object'"
          :data="offerDetail"
        />
      </template>
      <div class="mx-2 flex pr-3">
        <loader :is-loading="isUpdatingOfferSharedState" />
        <toggle-offer-shared
          v-if="showEditableControls"
          v-model="isShared"
          class="mt-1"
          data-cy="offer-shared"
        />
        <offer-note-prompt
          v-if="showEditableControls"
          :offer-detail="offerDetail"
          :funder="funder"
          :placement="placement"
          :is-closing="isClosing"
        />
        <div class="mt-0.5 ml-2">
          <offer-status-pill
            v-if="showStatusPill"
            :model-value="offerDetail.status"
            data-cy="offer-status-pill"
          />
          <offer-pill-dropdown
            v-else
            :value="offerDetail.status ?? ''"
            :model="offerDetail"
            :model-value="offerDetail.status ?? ''"
            data-cy="offer-pill-dropdown"
          />
        </div>
      </div>
    </div>
    <div class="ml-15 flex h-[5.3rem] w-full min-w-0">
      <div class="flex items-center">
        <div class="grid w-full min-w-0 auto-cols-fr grid-flow-col gap-4">
          <wfl-offer-field-display
            v-for="field in offerFields"
            :key="field.name"
            class="min-w-[200px]"
            :offer-field="field"
          />
        </div>
      </div>
    </div>
    <div
      v-if="showEditableControls"
      class="fixed-dropdown z-10 flex items-center justify-center border-l bg-white pl-2"
      @click.stop
    >
      <offer-dropdown-menu :model="offerDetail" :is-closing="isClosing" />
    </div>

    <offer-modal
      v-if="showOfferModal"
      :offer-details="[offerDetail.details]"
      edit-mode
      :is-closing="isClosing"
      :hide-offer-tab="isFunded"
      :view-only="viewOnly"
      @close="showOfferModal = false"
    />
  </div>
</template>
<script setup lang="ts" generic="T extends IOffer">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { Ability, PermissionSubject } from "@/enums/auth";
import { FieldType } from "@/enums/offers";
import { usePermissions } from "@/hooks/auth";
import { useToggleOfferSharedMutation } from "@/hooks/queries/offers";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";

import WflOfferFieldDisplay from "@/components/deals/workflow/OfferStage/WorkflowLive/WflOfferFieldDisplay.vue";
import Loader from "@/components/ui/Loader.vue";
import OfferPillDropdown from "@/components/ui/OfferPillDropdown.vue";
import OfferStatusPill from "@/components/ui/OfferStatusPill.vue";
import ToggleOfferShared from "@/components/ui/ToggleOfferShared.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import TableSelectDetails from "@/components/ui/table/TableSelectDetails.vue";
import OfferDropdownMenu from "@/views/deals/components/OfferDropdownMenu.vue";
import OfferModal from "@/views/deals/components/OfferModal.vue";
import OfferNotePrompt from "@/views/deals/components/OfferNotePrompt.vue";

import type { SelectDetailsProvider } from "@/models/common";
import type { IFunder, ILenderPlacement, IOffer } from "@/models/funders";
import type { OfferFieldDetails } from "@/models/offers";

const {
  offerDetail,
  funder,
  placement,
  isRowClickable,
  isClosing,
  viewOnly,
  multiSelect,
  singleSelect,
  isSelected,
} = defineProps<{
  offerDetail: T;
  funder?: IFunder;
  placement: ILenderPlacement;
  isRowClickable?: boolean;
  isClosing?: boolean;
  isFunded?: boolean;
  multiSelect?: boolean;
  singleSelect?: boolean;
  selectOneKey?: keyof T;
  selectDetailsProvider?: SelectDetailsProvider;
  viewOnly?: boolean;
  isSelected?: boolean;
}>();

const emit = defineEmits<{
  selectionChange: [IOffer, boolean];
}>();

const { canPerformActionReactive } = usePermissions();
const { hasStageTypeComputed } = useActiveWorkflowTemplate();

const {
  mutateAsync: toggleOfferSharedState,
  isPending: isUpdatingOfferSharedState,
} = useToggleOfferSharedMutation();

const { t } = useI18n();

const canUpdateOffer = canPerformActionReactive(
  PermissionSubject.offer,
  Ability.update,
  { offer: offerDetail.id }
);

const canUpdateOfferStatus = canPerformActionReactive(
  PermissionSubject.offer,
  Ability.updateStatus,
  { offer: offerDetail.id }
);

const isShared = computed({
  get: () => offerDetail.is_shared,
  set: () => changeSharedOffer(),
});

const showEditableControls = computed(
  () => !isClosing && !viewOnly && canUpdateOffer.value
);
const showStatusPill = computed(
  () => viewOnly || isClosing || !canUpdateOfferStatus.value
);
const showSelectionControls = computed(
  () => (multiSelect || singleSelect) && canUpdateOffer.value
);
const showOfferName = computed(() => !showSelectionControls.value);

const hasPlacementStage = hasStageTypeComputed("placement");

const showOfferModal = ref(false);
const isOfferSelected = computed({
  get: () => isSelected || false,
  set: (value) => {
    emit("selectionChange", offerDetail, value);
  },
});

const offerFields = computed(() => {
  const dataSelector = isClosing ? "closing_details" : "offer_details";

  const fields = offerDetail[dataSelector].reduce(
    (acc: OfferFieldDetails[], field) => {
      // Skip note fields
      if (field.name === "note") {
        return acc;
      }

      // For closing view with no closing details, use offer details values
      if (isClosing && !offerDetail.has_closing_details) {
        const offerField = offerDetail.offer_details.find(
          (offerField) => offerField.name === field.name
        );

        if (offerField) {
          acc.push({ ...field, value: offerField.value });
        }
        return acc;
      }

      acc.push(field);
      return acc;
    },
    []
  );

  // Additional fields configuration
  const additionalFieldsConfig = [
    {
      name: "sent_by",
      labelKey: "DEALS.TABLE_HEADERS.SENT_BY",
      value: offerDetail.sent_by?.full_name ?? "-",
    },
  ];

  if (hasPlacementStage.value) {
    additionalFieldsConfig.push(
      {
        name: "workflow_status",
        labelKey: "DEALS.TABLE_HEADERS.WORKFLOW_STATUS",
        value: offerDetail.placement.reference_workflow_status,
      },
      {
        name: "workflow_sub_status",
        labelKey: "DEALS.TABLE_HEADERS.WORKFLOW_SUB_STATUS",
        value: offerDetail.placement.reference_workflow_sub_status,
      }
    );
  }

  const additionalFields = additionalFieldsConfig.map((config, index) => ({
    name: config.name,
    label: t(config.labelKey),
    type: FieldType.STRING,
    value: config.value,
    weight: fields.length + index + 1,
  }));

  return [...fields, ...additionalFields];
});

const changeSharedOffer = async () => {
  await toggleOfferSharedState({ offerId: offerDetail.id });
};

const openModal = () => {
  if (isRowClickable) {
    showOfferModal.value = true;
  }
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.grid {
  grid-auto-columns: minmax(200px, 1fr);
}

.dropdown-wrapper {
  position: relative;
}

.fixed-dropdown {
  position: sticky;
  right: 0;
  z-index: 10;
}
</style>
